import React from 'react';
import { useTranslation } from 'react-i18next';
import LocationPicker from '../components/LocationPicker';
import { fieldOptionLabel, optionValue } from '../kinds';
import { Select, TextField } from '../ui';

/** A copy's own fields — what no source knows: the format (support for films, format for books) and the edition. */
export const COPY_KEYS = ['support', 'format', 'edition'];
/**
 * A video game's copy (phase 4 §4b.1), the platform first — a pile is
 * usually one shelf of one console: what ScanController::COPY_FIELDS keeps
 * on relink and sends with each add.
 */
export const GAME_COPY_KEYS = ['platform', 'region', 'completeness', 'media', 'edition', 'edition_details', 'condition'];
const TEXT_KEYS = ['edition', 'edition_details'];

/** The copy's keys of a type: a game's, else the format and the edition. */
export const copyKeysOf = (type) => (type && type.preset === 'videogame' ? GAME_COPY_KEYS : COPY_KEYS);

/** The copy's fields the type still uses, in that order (a game's edition is a select, a book's or a film's a text). */
export const copyFieldsOf = (type) => copyKeysOf(type)
  .map(key => ((type && type.fields) || []).find(field => field.key === key && !field.unused
    && (field.type === 'select' || (TEXT_KEYS.includes(key) && ['text', 'textarea'].includes(field.type || 'text')))))
  .filter(Boolean);

/** One copy field: a select of the type's options, or a text box. */
export function CopyField({ field, value, onChange, lang, emptyLabel, id }) {
  const { t } = useTranslation();
  if (field.type === 'select') {
    return (
      <Select label={field.name} value={value || ''} onChange={(event) => onChange(event.target.value)} id={id}>
        <option value="">{emptyLabel || t('itemForm.selectOption')}</option>
        {(field.options || []).map(option => (
          <option key={optionValue(option)} value={optionValue(option)}>{fieldOptionLabel(field, option, lang)}</option>
        ))}
      </Select>
    );
  }
  return <TextField label={field.name} value={value || ''} onChange={(event) => onChange(event.target.value)} id={id} />;
}

/**
 * The copy's fields and place for the next adds (one by one: remembered from
 * one add to the next; a pile: for every entry, unless a row says otherwise).
 */
function CopyFields({ fields, values, locationId, places, onChange, onLocation, lang, title }) {
  const { t } = useTranslation();
  return (
    <section className="scan-copy" aria-labelledby="scan-copy-title">
      <h2 id="scan-copy-title" className="scan-copy__title">{title}</h2>
      <div className="scan-copy__fields">
        {fields.map(field => (
          <CopyField key={field.key} field={field} lang={lang} value={values[field.key]} id={`scan-copy-${field.key}`}
                     onChange={(value) => onChange({ [field.key]: value })} />
        ))}
        {places && (
          <div className="item-field scan-copy__place">
            <label htmlFor="scan-copy-location" className="ui-field__label">{t('collection.location')}</label>
            <LocationPicker inputId="scan-copy-location" places={places} value={locationId} onChange={onLocation} />
          </div>
        )}
      </div>
    </section>
  );
}

export default CopyFields;
